import { NotificationProps } from '@components/Common/Notification';
import { useDeckContext } from '@context/DeckContext';
import { useUserContext } from '@context/UserContext';
import { createId } from '@paralleldrive/cuid2';
import {
  PublishIterationType,
  ScenarioManagementEntry,
  ScheduleType,
} from '@services/api/deckManagement/graphql/schema/API';
import { useActiveTOCEntriesByScheduleType } from '@services/api/deckManagement/hooks';
import { usePublishDeck, useSaveDeck } from '@services/api/deckManagement/hooks/useDeck';
import { useScenarioManagementEntries } from '@services/api/deckManagement/hooks/useScenarioManagement';
import { getDefaultDisplayMonth } from '@utils/dateHelpers';
import { isPublishIterationApplicable } from '@utils/deckCycleHelpers';
import {
  changeEntryField,
  initializeMetadata,
  markEntryAsDeleted,
  prepareEntriesForSaveDeckOperation,
  resetEntry,
} from '@utils/deckManagementEntityHelpers.ts';
import { cloneDeep, findIndex, isEmpty, isNil, isNull, uniq } from 'lodash';
import { createContext, ReactNode, SetStateAction, use, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChangeSetTargets,
  DeleteScenarioEntrySchema,
  SaveDeckChangeSetSchema,
  SaveDeckChangeSetType,
} from 'src/schema';
import { ScenarioManagementRowModel } from 'types/ScenarioManagement.types';
import {
  PublishData,
  ScenarioManagementContextType,
  ScenarioManagementProviderProps,
  ScenarioRowFilters,
} from 'types/ScenarioManagementContext.types';

/**
 * Reusable sort function for sorting ScenarioManagementRowModel by serial number (sno).
 * @param a
 * @param b
 */
const rowSorter = (a: ScenarioManagementRowModel, b: ScenarioManagementRowModel) =>
  parseInt(a.sno || '-1', 10) - parseInt(b.sno || '-1', 10);

export const ScenarioManagementContext = createContext<ScenarioManagementContextType>({
  scenarioEntries: null,
  deckCycleOptions: [],
  pageOptions: [],
  hasModifiedEntries: false,
  filteredScenarioEntries: [],
  updateScenarioEntry: () => {},
  publishScenarioEntry: () => {},
  handleNewEntry: () => {},
  handleDeleteEntry: () => {},
  handleModifyEntry: () => {},
  handleSaveChanges: () => {},
  handleDiscardChanges: () => {},
  isSaving: false,
  isLoading: true,
  isError: false,
  error: null,
  hasInitialDataLoaded: false,
  scenarioRowFilters: {
    scheduleType: ScheduleType.APPROVED,
    month: '1',
    deckCycle: '',
    publishIterationType: PublishIterationType.DEFAULT,
  },
  setScenarioRowFilters: () => {},
  systemRunInProgress: false,
  publishData: null,
  isMonthApplicable: true,
});

export const ScenarioManagementContextProvider = ({ children }: ScenarioManagementProviderProps): ReactNode => {
  const { username } = useUserContext();
  const { currentDeckId, currentDeck, isError: isDeckError } = useDeckContext();
  // NOTE: setScenarioRowFiltersState is wrapped by `setScenarioRowFilters()` below
  const [scenarioRowFilters, setScenarioRowFiltersState] = useState<ScenarioRowFilters>({
    scheduleType: ScheduleType.APPROVED,
    month: getDefaultDisplayMonth(),
    deckCycle: currentDeck?.deck_cycle || currentDeck?.publish_deck_cycle || '', // Set to default of Deck available cycle information (deck_cycle or publish_deck_cycle) if it exists
    publishIterationType: PublishIterationType.DEFAULT,
  });
  const [deckCycleOptions, setDeckCycleOptions] = useState<string[]>([]);
  const [hasModifiedEntries, setHasModifiedEntries] = useState<boolean>(false);
  const [scenarioEntryRows, setScenarioEntryRows] = useState<ScenarioManagementRowModel[]>([]);
  const [newEntries, setNewEntries] = useState<ScenarioManagementRowModel[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [filteredScenarioEntries, setFilteredScenarioEntries] = useState<ScenarioManagementRowModel[]>([]);
  const [deckId, setDeckId] = useState<string>('');
  const [notification, setNotification] = useState<NotificationProps | undefined>(undefined);
  const [publishData, setPublishData] = useState<PublishData | null>(null);

  // Tracks first data load of Scenario data items
  const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(false); // To resolve seeing no TOC items loaded message before Api is done

  const { mutate: saveDeckMutate } = useSaveDeck();
  const { mutate: publishDeckMutate } = usePublishDeck();

  const { data: tocEntries } = useActiveTOCEntriesByScheduleType(currentDeckId || '', ScheduleType.MODIFIED, true, {
    enabled: !isEmpty(currentDeckId) && !isDeckError,
  });

  const {
    data: rawScenarioEntries = null,
    isLoading,
    isError,
    isFetched,
    error,
    refetch,
  } = useScenarioManagementEntries(deckId, { enabled: !isEmpty(deckId) && !isDeckError });

  /**
   * Update publishData when currentDeck changes
   */
  useEffect(() => {
    setPublishData((): PublishData | null => {
      if (!isNil(currentDeck?.publish_status)) {
        return {
          publishUser: currentDeck?.publish_user || null,
          publishTimestamp: currentDeck?.publish_timestamp || null,
          lastPublishDate: currentDeck?.last_publish_date || null,
          publishStatus: currentDeck?.publish_status,
        };
      }
      return null;
    });
  }, [
    currentDeck?.last_publish_date,
    currentDeck?.publish_status,
    currentDeck?.publish_timestamp,
    currentDeck?.publish_user,
  ]);

  const { scheduleType } = scenarioRowFilters;

  const setScenarioRowFilters = useCallback((action: SetStateAction<ScenarioRowFilters>) => {
    setScenarioRowFiltersState(action);
    // Always empty the filtered entries when row filters are modified.
    // Fixes a bug with "warning" icons briefly appears when we switch from "Approved" to "Modified Schedule" data.
    // See: https://taskei.amazon.dev/tasks/P322342609
    setFilteredScenarioEntries([]);
  }, []);

  /**
   * Handles sorting of the deckCycleOptions list.
   * TODO: Will need refactoring for other decks. Currently only supports "Day#" formatted options ("Day7").
   * @param a
   * @param b
   */
  const deckCycleOptionsSortHandler = (a: string, b: string) => {
    const numA = parseInt(a.replace('Day', ''), 10);
    const numB = parseInt(b.replace('Day', ''), 10);
    return numA - numB;
  };

  /**
   * Determines if month filtering is applicable based on the scenario entries.
   * Decks like OP1/OP2/GQ1/GQ2 have empty month values, making the month filter irrelevant.
   */
  const isMonthApplicable = useMemo(() => {
    if (!rawScenarioEntries || rawScenarioEntries.length === 0) return true;
    return rawScenarioEntries.some((entry) => !isEmpty(entry.month));
  }, [rawScenarioEntries]);

  /**
   * Handler for Array.filter() that controls which rows to include based on values from scenarioRowFilters
   */
  const rowFilterHandler = useCallback(
    (row: ScenarioManagementRowModel) => {
      return (
        // ScheduleType Filter
        (!scenarioRowFilters.scheduleType || row.schedule_type === scenarioRowFilters.scheduleType) &&
        // Month Filter — skip entirely when the deck doesn't use months (e.g., OP1/OP2/GQ1/GQ2 decks)
        (!isMonthApplicable || !scenarioRowFilters.month || row.month === scenarioRowFilters.month) &&
        // DeckCycle Filter
        (!scenarioRowFilters.deckCycle || row.deck_cycle === scenarioRowFilters.deckCycle)
      );
    },
    [scenarioRowFilters, isMonthApplicable],
  );

  /**
   * When data is available for tocEntries, process the list to generate pageOptions.
   */
  const pageOptions = useMemo(() => {
    if (tocEntries && tocEntries.length > 0) {
      return (
        tocEntries
          // Only the Modified Schedule pages matter
          // TODO: Currently change ScheduleType.APPROVED to ScheduleType.MODIFIED once data is available
          .filter((entry) => entry.schedule_type === ScheduleType.MODIFIED && entry.page_name)
          // Just need the page names
          .map((entry) => entry.page_name!)
      );
    }
    return [];
  }, [tocEntries]);

  /**
   * Handles clearing out the current notification object when it is closed
   */
  const handleCloseNotification = useCallback(() => {
    setNotification(undefined);
  }, []);

  /**
   * Updates Scenario Entries
   * TODO: Implement
   */
  const updateScenarioEntry = useCallback(() => {
    throw new Error('[NOT IMPLEMENTED]: ScenarioManagementContextProvider.updateScenarioEntry');
  }, []);

  /**
   * Handles publishing Scenario Entries
   */
  const publishScenarioEntry = useCallback(() => {
    publishDeckMutate(
      {
        deckId: currentDeckId!,
        publish_id: createId(),
        schedule_type: scenarioRowFilters.scheduleType,
        month:
          isMonthApplicable && scenarioRowFilters.month
            ? new Intl.DateTimeFormat('en-US', { month: 'long' }).format(
                new Date(2000, Number(scenarioRowFilters.month) - 1, 1),
              )
            : '',
        deck_cycle: scenarioRowFilters.deckCycle,
        publish_iteration_type: isPublishIterationApplicable(scenarioRowFilters.deckCycle)
          ? (scenarioRowFilters.publishIterationType ?? PublishIterationType.DEFAULT)
          : PublishIterationType.DEFAULT,
      },
      {
        onError: (publishDeckError) => {
          if (publishDeckError.message.includes('Month mismatch:')) {
            setNotification({
              message: 'Month mismatch',
              onClose: handleCloseNotification,
              visible: true,
              type: 'error',
            });
          }
        },
      },
    );
  }, [
    publishDeckMutate,
    currentDeckId,
    scenarioRowFilters.scheduleType,
    scenarioRowFilters.month,
    scenarioRowFilters.deckCycle,
    scenarioRowFilters.publishIterationType,
    isMonthApplicable,
    handleCloseNotification,
  ]);

  /**
   * Handles saving changes (new/modified/deleted entries) to service
   */
  const handleSaveChanges = useCallback(() => {
    setIsSaving(true);
    const updatedEntries = scenarioEntryRows
      .filter((item) => item.metadata?.isModified)
      .map(
        (item) =>
          ({
            ...item,
            deck_id: currentDeckId!,
            // TODO [QUESTION]: Can `scenario_id` be stored by the service during the `create` request, and retrieved in the GraphQL?
            //    Otherwise, we have to do this to populate a field that is restricted from being updated to begin with.
            scenario_id: item.scenario_id || item.sk!.split('#')[2],
            schedule_type: item.schedule_type || ScheduleType.MODIFIED, // Ensure a schedule type is assigned as ScenarioManagementItemsInput requires it
          }) as ScenarioManagementRowModel,
      );

    const preppedNewEntries = newEntries.map(
      (item) =>
        ({
          ...item,
          deck_id: currentDeckId!,
          scenario_id: item.metadata.internalId,
        }) as ScenarioManagementRowModel,
    );

    const finalProcessing = prepareEntriesForSaveDeckOperation(
      [...updatedEntries, ...preppedNewEntries],
      // Using the DeleteScenarioEntrySchema Zod Schema because it represents the fields used by the backend service
      // in order to generate `pk` and `sk` values behind-the-scenes. As such, those fields are the protected ones.
      Object.keys(DeleteScenarioEntrySchema.shape) as Array<keyof ScenarioManagementRowModel>,
      {
        scenario_id: (entry) => entry.metadata.internalId,
      },
    );

    // Parse/Validate the changeSet against the Zod schemas.
    const changeSet: SaveDeckChangeSetType = SaveDeckChangeSetSchema.parse({
      [ChangeSetTargets.scenario]: {
        ...(!isEmpty(finalProcessing.create) ? { create: finalProcessing.create } : {}),
        ...(!isEmpty(finalProcessing.delete) ? { delete: finalProcessing.delete } : {}),
        ...(!isEmpty(finalProcessing.update) ? { update: finalProcessing.update } : {}),
      },
    });

    saveDeckMutate(
      {
        deckId: currentDeckId!,
        deckName: currentDeck?.deck_name || undefined,
        change_set: changeSet,
        currentUser: username,
      },
      {
        onSuccess: () => {
          setHasModifiedEntries(false);
          // Reset these states and refetch data
          refetch().then(() => {
            setNewEntries([]);
            setIsSaving(false);
            setNotification({
              message: 'Saved Successfully!',
              onClose: handleCloseNotification,
              visible: true,
              type: 'success',
            });
          });
        },
        onError: () => {
          setIsSaving(false);
          setNotification({
            message: 'An Error Has Occurred',
            onClose: handleCloseNotification,
            visible: true,
            type: 'error',
          });
        },
      },
    );
  }, [
    scenarioEntryRows,
    newEntries,
    saveDeckMutate,
    currentDeckId,
    currentDeck?.deck_name,
    refetch,
    handleCloseNotification,
    username,
  ]);

  /**
   * Resets all entries to original values and erases all newEntries.
   */
  const handleDiscardChanges = useCallback(() => {
    setScenarioEntryRows((prevState) => prevState.map(resetEntry).sort(rowSorter));
    setNewEntries([]);
  }, []);

  /**
   * When a row is modified, we need to add it to the scenarioEntryRows state variable, and update the metadata
   * to reflect the change.
   * TODO: Audit Log Entry Structures
   */
  const handleModifyEntry = useCallback(
    <K extends keyof ScenarioManagementRowModel>(
      internalId: string,
      field: K,
      newValue: ScenarioManagementRowModel[K],
    ) => {
      // Check new entries for internalId
      setNewEntries((prevNewEntries) => {
        const newEntryIndex = prevNewEntries.findIndex((row) => row.metadata.internalId === internalId);
        if (newEntryIndex > -1) {
          const newState = [...prevNewEntries];
          const entry = prevNewEntries[newEntryIndex];
          newState[newEntryIndex] = changeEntryField(entry, field, newValue);
          return newState;
        }
        return prevNewEntries;
      });

      // Check existing entries for internalId
      setScenarioEntryRows((prevScenarioEntryRows) => {
        const entryIndex = prevScenarioEntryRows.findIndex((row) => row.metadata.internalId === internalId);
        if (entryIndex > -1) {
          const newState = [...prevScenarioEntryRows];
          const entry = prevScenarioEntryRows[entryIndex];
          newState[entryIndex] = changeEntryField(entry, field, newValue);
          return newState;
        }
        return prevScenarioEntryRows;
      });
    },
    [],
  );

  /**
   * When a row is deleted, we need to make the entry as delete within the ScenarioEntryRows.
   * NOTE: If the entry was a newEntry (never saved to backend service) we only remove it from the newEntries list.
   */
  const handleDeleteEntry = useCallback((isNewRow: boolean, entryId: string) => {
    if (isNewRow) {
      setNewEntries((prevState) => {
        return [...prevState.filter((item) => item.metadata?.internalId !== entryId)];
      });
    } else {
      setScenarioEntryRows((prevScenarioEntryRows) => {
        const entryIndex = findIndex(prevScenarioEntryRows, (item) => item.metadata?.internalId === entryId);
        if (entryIndex > -1) {
          const newRows = cloneDeep(prevScenarioEntryRows);
          newRows[entryIndex] = markEntryAsDeleted(newRows[entryIndex]);
          return newRows;
        }
        return prevScenarioEntryRows;
      });
    }
  }, []);

  /**
   * When a new row is added, we need to add it to the newEntries state variable
   */
  const handleNewEntry = useCallback(() => {
    setNewEntries((prevEntries) => {
      // TODO: Establish default values properly
      const newEntry: ScenarioManagementRowModel = initializeMetadata(
        {
          page_no: '0', // <-- NOTE: This gets populated accurately once the row is rendered in AGGrid.
          month: isMonthApplicable ? scenarioRowFilters.month : null,
          deck_cycle: scenarioRowFilters.deckCycle,
          page_name: pageOptions[0],
          page_grain: 'Summary', // TODO: Need to compile PageGrain options
          is_external_page: false,
          schedule_type: ScheduleType.MODIFIED,
        } as ScenarioManagementEntry,
        { isNewRow: true },
      );
      return [...prevEntries, newEntry];
    });
  }, [pageOptions, scenarioRowFilters.deckCycle, scenarioRowFilters.month, isMonthApplicable]);

  /**
   * Checks the presences of modified entries which need saved to backend service.
   */
  useEffect(() => {
    setHasModifiedEntries(!isEmpty(newEntries) || scenarioEntryRows.some((row) => row.metadata?.isModified));
  }, [scenarioEntryRows, newEntries]);

  /**
   * When currentDeckId changes, update the deckId state variable and reset our scenario management entries.
   */
  useEffect(() => {
    if (currentDeckId && currentDeckId !== deckId) {
      setScenarioEntryRows([]);
      setDeckId(currentDeckId);
    }
  }, [currentDeckId, deckId]);

  /**
   * When deckCycleOptions is populated and no current value is set in scenarioRowFilters, assign one.
   */
  useEffect(() => {
    if (!isEmpty(deckCycleOptions) && !scenarioRowFilters.deckCycle) {
      setScenarioRowFiltersState((prevState) => {
        return { ...prevState, deckCycle: deckCycleOptions[0] };
      });
    }
  }, [deckCycleOptions, scenarioRowFilters.deckCycle]);

  /**
   * When rawScenarioEntries is first populated (and scenarioEntryRows is empty), we need to populate scenarioEntryRows
   * when ScenarioManagementRowModel objects, applying a cuid2 to metadata.internalId for internal tracking.
   */
  useEffect(() => {
    if (!isLoading && isFetched && rawScenarioEntries) {
      const scenarioEntries: ScenarioManagementRowModel[] = [];
      const deckCycles: string[] = [];

      rawScenarioEntries.forEach((entry: ScenarioManagementEntry) => {
        scenarioEntries.push(initializeMetadata<ScenarioManagementRowModel>(entry));
        if (entry.deck_cycle) {
          deckCycles.push(entry.deck_cycle);
        }
      });

      scenarioEntries.sort(rowSorter);

      setScenarioEntryRows(scenarioEntries);
      const cycleOpts = uniq(deckCycles).sort(deckCycleOptionsSortHandler);
      setDeckCycleOptions(isEmpty(cycleOpts) ? [] : cycleOpts);
      // Mark initial data as loaded since we have data
      setHasInitialDataLoaded(true);
    }
  }, [isLoading, isFetched, rawScenarioEntries]); // <--- DO NOT INCLUDE `scenarioEntryRows` HERE

  /**
   * Handles the case where no value currently exists for deckCycle in Scenario Row Filters
   * Sets default Deck Cycle in this order: Deck deck_cycle (if exists), Deck publish_deck_cycle (if exists), or use the first option available from generated DeckCycle Options list.
   */
  useEffect(() => {
    if (isEmpty(scenarioRowFilters.deckCycle)) {
      const defaultCycle = currentDeck?.deck_cycle || currentDeck?.publish_deck_cycle || deckCycleOptions[0] || '';
      setScenarioRowFiltersState((prevState) => {
        return { ...prevState, deckCycle: defaultCycle };
      });
    }
  }, [deckCycleOptions, scenarioRowFilters.deckCycle, currentDeck?.deck_cycle, currentDeck?.publish_deck_cycle]);

  /**
   * When scenarioEntries is populated and scheduleType changes, update the filteredScenarioEntries list.
   */
  useEffect(() => {
    // Note: make sure we have a selected deckCycle option to prevent initial filtering.
    if (!isLoading && rawScenarioEntries && scenarioEntryRows && scenarioRowFilters.deckCycle) {
      let filteredResults = scenarioEntryRows.filter(rowFilterHandler);

      if (scheduleType === ScheduleType.MODIFIED) {
        // Exclude deleted entries
        filteredResults = filteredResults.filter((entry) => !entry.metadata.isDeleted);

        // Include newEntries when working with MODIFIED ScheduleType
        filteredResults.push(...newEntries.filter(rowFilterHandler));
      }
      setFilteredScenarioEntries(filteredResults.sort(rowSorter));
    }
  }, [
    isLoading,
    newEntries,
    rawScenarioEntries,
    rowFilterHandler,
    scenarioEntryRows,
    scenarioRowFilters.deckCycle,
    scheduleType,
  ]);

  /**
   * Create the context value object
   *
   * Combines all state variables and methods into a single object that will be
   * provided to consumers of the context. This ensures that the context value
   * only changes when one of its dependencies changes, preventing unnecessary
   * re-renders of components that consume the context.
   */
  const contextValue = useMemo<ScenarioManagementContextType>(
    () => ({
      scenarioEntries: rawScenarioEntries,
      pageOptions,
      deckCycleOptions,
      hasModifiedEntries,
      filteredScenarioEntries,
      updateScenarioEntry,
      publishScenarioEntry,
      handleNewEntry,
      handleDeleteEntry,
      handleModifyEntry,
      handleSaveChanges,
      handleDiscardChanges,
      isSaving,
      isLoading: isLoading || (isNull(rawScenarioEntries) && !isError),
      isError,
      error,
      hasInitialDataLoaded,
      scenarioRowFilters,
      setScenarioRowFilters,
      notification,
      systemRunInProgress: currentDeck?.has_active_system_run ?? false,
      publishData,
      isMonthApplicable,
    }),
    [
      rawScenarioEntries,
      pageOptions,
      deckCycleOptions,
      hasModifiedEntries,
      filteredScenarioEntries,
      updateScenarioEntry,
      publishScenarioEntry,
      handleNewEntry,
      handleDeleteEntry,
      handleModifyEntry,
      handleSaveChanges,
      handleDiscardChanges,
      isSaving,
      isLoading,
      isError,
      error,
      hasInitialDataLoaded,
      scenarioRowFilters,
      setScenarioRowFilters,
      notification,
      currentDeck,
      publishData,
      isMonthApplicable,
    ],
  );

  return <ScenarioManagementContext value={contextValue}>{children}</ScenarioManagementContext>;
};

/**
 * Custom hook to access the scenario management context
 *
 * @returns {ScenarioManagementContextType} The scenario management context value
 */
export const useScenarioManagementContext = (): ScenarioManagementContextType => {
  return use(ScenarioManagementContext);
};
